/**
 * Comment Event Bus - Synchronizes comment state changes across all components
 *
 * This event bus ensures that when a comment is liked, disliked, or updated on one page/component,
 * all other instances of that comment are immediately updated without needing to refresh.
 */

type CommentEventType =
  | 'comment:like'
  | 'comment:unlike'
  | 'comment:dislike'
  | 'comment:undislike'
  | 'comment:update'
  | 'comment:delete';

interface CommentLikeEvent {
  commentId: number;
  liked: boolean;
  likes: number;
  userId?: string;
}

interface CommentDislikeEvent {
  commentId: number;
  disliked: boolean;
  dislikes: number;
  userId?: string;
}

interface CommentUpdateEvent {
  commentId: number;
  updates: Partial<{
    content?: string;
    likes?: number;
    dislikes?: number;
    liked?: boolean;
    disliked?: boolean;
  }>;
}

interface CommentDeleteEvent {
  commentId: number;
}

type CommentEventData =
  | CommentLikeEvent
  | CommentDislikeEvent
  | CommentUpdateEvent
  | CommentDeleteEvent;

type EventListener = (data: CommentEventData) => void;

class CommentEventBus {
  private listeners: Map<CommentEventType, Set<EventListener>> = new Map();

  /**
   * Subscribe to comment events
   */
  on(event: CommentEventType, callback: EventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from comment events
   */
  off(event: CommentEventType, callback: EventListener): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  /**
   * Emit a comment event to all subscribers
   */
  emit(event: CommentEventType, data: CommentEventData): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
  }

  /**
   * Emit a like event
   */
  emitLike(commentId: number, liked: boolean, likes: number, userId?: string): void {
    this.emit(liked ? 'comment:like' : 'comment:unlike', {
      commentId,
      liked,
      likes,
      userId
    });
  }

  /**
   * Emit a dislike event
   */
  emitDislike(commentId: number, disliked: boolean, dislikes: number, userId?: string): void {
    this.emit(disliked ? 'comment:dislike' : 'comment:undislike', {
      commentId,
      disliked,
      dislikes,
      userId
    });
  }

  /**
   * Emit a comment update event
   */
  emitUpdate(commentId: number, updates: Partial<any>): void {
    this.emit('comment:update', {
      commentId,
      updates
    });
  }

  /**
   * Emit a comment delete event
   */
  emitDelete(commentId: number): void {
    this.emit('comment:delete', {
      commentId
    });
  }

  /**
   * Clear all listeners (useful for cleanup/testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

// Export singleton instance
export const commentEventBus = new CommentEventBus();

// Export types for use in components
export type {
  CommentEventType,
  CommentLikeEvent,
  CommentDislikeEvent,
  CommentUpdateEvent,
  CommentDeleteEvent,
  CommentEventData,
  EventListener
};
